import React, { useState, useEffect } from 'react';

import useAsync from 'components/hooks/useAsync';
import { addMonths, format, parseISO, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import ActionsContainer from 'screens/admin/Reviews/ReviewProcess/ReviewCycle/actionsContainer';
import DateRangeSelectionDropdown from 'screens/analytics/DateRangeSelectionDropdown';
import styled from 'styled-components';
import { FlexContainer, GutterCSS, StyledTypography } from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { thisMonth, upcomingMonth, upcomingWeek, upcomingQuarter, thisQuarter } from 'constants/ProjectConstants';
import { convertDateToString4 } from 'constants/helper';
import theme from 'constants/theme';

import { getSettings, setSettings } from 'services/engagement';
import { showErrorSnackbar } from 'services/snackbar';

import BooleanRadioGroup from 'uiToolkit/BooleanRadioGroup';

import { DeerFooterContainer } from './DeerFooterContainer';

const predefinedRanges = [upcomingWeek, thisMonth, upcomingMonth, thisQuarter, upcomingQuarter];

const Divider = styled.div`
  border-bottom: 1px solid ${theme.palette.borders.lighterGrey};
  ${GutterCSS}
`;

const FormContainer = styled.div`
  background: white;
  border: 1px solid #eaeaea;
  margin-top: 1.5rem;
  margin-bottom: 5rem;
  padding: 1rem 1.5rem 2.5rem 1.5rem;
`;

const Settings = ({ formId, backFn, nextFn, isDraft }) => {
  const { isLoading, run } = useAsync();
  const dispatch = useDispatch();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setendDate] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isPublic, setIsPublic] = useState(false);

  useEffect(() => {
    run(
      getSettings({ surveyId: formId }).then(({ startDate, endDate, isAnonymous, isPublic }) => {
        if (startDate) {
          // select only date part
          const datePart = startDate.split('T')[0];
          setStartDate(parseISO(datePart));
        }
        if (endDate) {
          // select only date part
          const datePart = endDate.split('T')[0];
          setendDate(parseISO(datePart));
        }
        setIsAnonymous(isAnonymous || false);
        setIsPublic(isPublic || false);
      })
    );
  }, [formId, run]);

  const save = () => {
    const formattedStartDate = format(startDate, 'yyyy-MM-dd');
    const formattedEndDate = format(endDate, 'yyyy-MM-dd');

    setSettings({
      surveyId: formId,
      body: { startDate: formattedStartDate, endDate: formattedEndDate, isAnonymous, isPublic },
    })
      .then((res) => {
        nextFn();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar('Sorry, unable to save settings!'));
      });
  };

  return (
    <>
      <StyledTypography variant="h5" customColor={theme.palette.text.heading} bottomGutter={1}>
        Settings
      </StyledTypography>
      <StyledTypography variant="body" customColor={theme.palette.text.label}>
        Configure settings for the engagement
      </StyledTypography>
      <FormContainer>
        <StyledTypography customColor={theme.palette.text.heading} topGutter={2}>
          <b>Survey date</b>
        </StyledTypography>

        <FlexContainer style={{ alignItems: 'center' }} topGutter={2}>
          <label style={{ cursor: 'pointer' }} htmlFor="add-date">
            <StyledTypography customColor="textPrimary" rightGutter={2}>
              Select start and end date
            </StyledTypography>
          </label>

          <DateRangeSelectionDropdown
            id="add-date"
            defaultDateRange={upcomingWeek}
            onDateRangeChange={(dates) => {
              setStartDate(dates.startDate);
              setendDate(dates.endDate);
            }}
            predefinedRanges={predefinedRanges}
            minDate={startOfDay(new Date())}
            maxDate={addMonths(startOfDay(new Date()), 11)}
            showDropdownArrow={false}
            buttonText={
              startDate
                ? `${convertDateToString4(new Date(startDate))}-${convertDateToString4(new Date(endDate))}`
                : 'Add date'
            }
            popupPlacement="right-start"
            disabled
          />
        </FlexContainer>

        <Divider topGutter={2} bottomGutter={1} />
        <StyledTypography customColor={theme.palette.text.heading} topGutter={2}>
          <b>Employee privacy</b>
        </StyledTypography>
        <BooleanRadioGroup
          text1="Take responses anonymously"
          text2="Record employee's name"
          isEnabled={isAnonymous}
          onChangeHandler={setIsAnonymous}
          disableInput={!isDraft}
        />
        <Divider topGutter={2} bottomGutter={1} />
        <StyledTypography customColor={theme.palette.text.heading} topGutter={3}>
          <b>Analytics deliverables</b>
        </StyledTypography>
        <BooleanRadioGroup
          text1="Share survey result with everyone"
          text2="Do not share final report with everyone"
          isEnabled={isPublic}
          onChangeHandler={setIsPublic}
        />
      </FormContainer>
      <ActionsContainer
        ContainerComp={checkNewNavEnabled() ? DeerFooterContainer : null}
        loading={isLoading}
        onBackClick={backFn}
        onNextClick={startDate && endDate && save}
        backText="back"
        nextDisabled={false}
      />
    </>
  );
};

export default Settings;
